import { isDefined } from "@omni/utils/helper.utils";
import { entries } from "lodash-es";

import {
    Facet,
    isFacetType
} from "@/components/Search/SearchResults/StateComponents/Facets/Facet/@types/facet.types";
import {
    FacetType,
    FacetTypeToValueMap,
    RecursiveHierarchicalFacet
} from "@/components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import { SearchConfiguration } from "@/redux/modules/value/search/search.types";
import { BffSearchResponseTransformer } from "@/services/bffSearch/applyTransformers";
import { SearchComponentOfType } from "@/utils/search/types/searchComponent";
import { SearchComponentStateMap } from "@/utils/search/types/searchState";

export const HIERARCHICAL_FACET_DELIMITER = "###";
const HIERARCHICAL_FACET_SEPARATOR = " > ";
export const HIERARCHICAL_FACET_SUFFIX = ".lvl";
const HIERARCHICAL_FACET_SUFFIX_REGEX = new RegExp(
    `${HIERARCHICAL_FACET_SUFFIX}(\\d+)$`
);

type HierarchicalFacetValue = {
    facetValues: Array<{
        values: Record<string, number>;
        level: number;
    }>;
    order?: number;
    index: string;
};
type FacetNameToHierarchicalFacetValuesMap = Record<
    string,
    HierarchicalFacetValue
>;

export const extractValue = (formattedValue: string) => {
    const [_, identifier, textValue] = formattedValue.split(
        HIERARCHICAL_FACET_DELIMITER
    );

    return {
        identifier: identifier,
        textValue: textValue
    };
};

export const getHierarchicalFacetName = (facetName: string) =>
    facetName.replace(HIERARCHICAL_FACET_SUFFIX_REGEX, "");

export const getHierarchicalFacetLevel = (facetName: string) => {
    const levelAsString = facetName
        .match(HIERARCHICAL_FACET_SUFFIX_REGEX)
        ?.at(1);
    if (!levelAsString) return;
    return parseInt(levelAsString, 10);
};
export const toHierarchicalFacetName = (
    hierarchicalName: string,
    level: number
) => `${hierarchicalName}${HIERARCHICAL_FACET_SUFFIX}${level.toString()}`;

export const toHierarchicalFacetPath = (path: string) =>
    path.split(HIERARCHICAL_FACET_SEPARATOR);

export const toHierarchicalFacet = (path: Array<string>) =>
    path.join(HIERARCHICAL_FACET_SEPARATOR);

const getRecursiveHierarchicalFacetByPath = (
    root: RecursiveHierarchicalFacet,
    path: Array<string>
) =>
    path.reduce(
        (recursiveHierarchicalFacetValue, segment) =>
            recursiveHierarchicalFacetValue?.childValues[segment] ??
            recursiveHierarchicalFacetValue,
        root
    );

const extractHierarchicalFacetValuesMap = (
    facets: Array<Facet<FacetType>>
): FacetNameToHierarchicalFacetValuesMap => {
    const map = facets.reduce(
        (facetNameToHierarchicalFacetValuesMap, facet) => {
            if (!isFacetType(FacetType.HIERARCHICAL, facet)) {
                return facetNameToHierarchicalFacetValuesMap;
            }

            const facetName = getHierarchicalFacetName(facet.name);
            const level = getHierarchicalFacetLevel(facet.name);
            if (!isDefined(level)) return facetNameToHierarchicalFacetValuesMap;

            if (!isDefined(facetNameToHierarchicalFacetValuesMap[facetName])) {
                facetNameToHierarchicalFacetValuesMap[facetName] = {
                    facetValues: [],
                    index: facet.index
                };
            }

            const hierarchicalFacet =
                facetNameToHierarchicalFacetValuesMap[facetName];
            if (level === 0) hierarchicalFacet.order = facet.order;
            hierarchicalFacet.facetValues.push({
                values: facet.values as unknown as Record<string, number>,
                level: level
            });

            return facetNameToHierarchicalFacetValuesMap;
        },
        {} as FacetNameToHierarchicalFacetValuesMap
    );

    Object.values(map).forEach((hierarchicalFacetValue) =>
        hierarchicalFacetValue.facetValues.sort((a, b) => a.level - b.level)
    );

    return map;
};

const hierarchicalFacetValueToRecursiveHierarchicalFacet = (
    hierarchicalFacetValue: HierarchicalFacetValue
) =>
    hierarchicalFacetValue.facetValues.reduce(
        (recursiveHierarchicalFacet, facetValue) => {
            Object.entries(facetValue.values).forEach(([stringPath, count]) => {
                const path = toHierarchicalFacetPath(stringPath);
                if (!path.length) return;

                const recursiveHierarchicalFacetByPath =
                    getRecursiveHierarchicalFacetByPath(
                        recursiveHierarchicalFacet,
                        path
                    );

                recursiveHierarchicalFacetByPath.childValues[path.at(-1)!] = {
                    count: count,
                    facetValue: stringPath,
                    childValues: {},
                    parentValue: recursiveHierarchicalFacetByPath.facetValue
                        ? recursiveHierarchicalFacetByPath
                        : null
                };
            });

            return recursiveHierarchicalFacet;
        },
        { childValues: {} } as RecursiveHierarchicalFacet
    );

const removeEntriesFromDifferentCategoryTree = (
    searchComponentState: SearchComponentStateMap[SearchComponentOfType<"FACETS">],
    searchConfiguration: SearchConfiguration
) =>
    searchComponentState.facets
        .filter((facet) => facet.type === FacetType.HIERARCHICAL)
        .map((facet) => {
            const categoryPagePaths = searchConfiguration.categoryPagePaths;

            if (!categoryPagePaths?.length) return facet;

            return {
                ...facet,
                values: Object.fromEntries(
                    entries(facet.values).filter(([stringPath]) => {
                        const facetPath = toHierarchicalFacetPath(stringPath)
                            .map(extractValue)
                            .map(({ identifier }) => identifier)
                            .join(HIERARCHICAL_FACET_SEPARATOR);

                        return !!categoryPagePaths.find((categoryPagePath) =>
                            facetPath.startsWith(categoryPagePath)
                        );
                    })
                ) as FacetTypeToValueMap[FacetType.HIERARCHICAL]
            };
        });

const mergeHierarchicalFacetsTransformer: BffSearchResponseTransformer<
    SearchComponentOfType<"FACETS">
> = (searchComponentState, searchConfiguration) => {
    const hierarchicalFacetsFromCategoryTree =
        removeEntriesFromDifferentCategoryTree(
            searchComponentState,
            searchConfiguration
        );

    const hierarchicalFacetValuesMap = extractHierarchicalFacetValuesMap(
        hierarchicalFacetsFromCategoryTree
    );

    const hierarchicalFacets = Object.entries(hierarchicalFacetValuesMap).map(
        ([facetName, facet]) =>
            ({
                name: facetName,
                type: FacetType.HIERARCHICAL,
                order: facet.order,
                values: hierarchicalFacetValueToRecursiveHierarchicalFacet(
                    facet
                ).childValues,
                index: facet.index
            }) as Facet<FacetType.HIERARCHICAL>
    );

    return {
        ...searchComponentState,
        facets: [
            ...hierarchicalFacets,
            ...searchComponentState.facets.filter(
                (facet) => facet.type !== FacetType.HIERARCHICAL
            )
        ]
    };
};

export { mergeHierarchicalFacetsTransformer };
